import { UserCompanyDto, MeUserDto, Role } from '@interfaces/models';
import { useNavigate, useParams } from 'react-router-dom';
import { csManager, lsManager } from '@shared/utils';
import { currentUserDetails } from '@api/users';
import { Language } from '@shared/global/i18n';
import { useUserStore } from '@shared/stores';
import * as Sentry from '@sentry/react';
import { useAuth } from '@shared/hooks';
import { useMemo } from 'react';

export type UserMetadata = ({ company?: UserCompanyDto } & MeUserDto) | null;

export const useCurrentUser = (): {
  setUserLanguage: (language: Language) => void;
  setUserCompany: (companyId: string) => void;
  setUser: (data: MeUserDto | null) => void;
  companyId: string | null;
  userLogout: () => void;
  user: UserMetadata;
  role: Role | null;
  currency: string;
} => {
  const { user: rawUserData, setUser } = useUserStore();
  const { companyId: rawCompanyId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const userLogout = (): void => {
    logout();
    csManager.remove('SESSION_ID');
  };

  const setUserLanguage = (language: Language): void => {
    lsManager.set('LANGUAGE', language);
    // make sure that user language is updated in the backend
    currentUserDetails().then(() => window.location.reload());
  };

  const setUserCompany = (companyId: string): void => {
    navigate(`/${companyId}`);
  };

  const user: UserMetadata = useMemo(() => {
    if (!rawUserData) return null;
    if (!rawCompanyId) return rawUserData;

    const company = rawUserData.companies?.find((company) => company.id === rawCompanyId);

    Sentry.setUser({ email: rawUserData?.email, id: rawUserData?.id });
    Sentry.setContext('company', company ? { id: company.id, role: company.role } : null);

    return { ...rawUserData, company };
  }, [rawCompanyId, rawUserData]);

  const currency = user?.company?.currency || 'PLN';
  const companyId = user?.company?.id || null;
  const role = user?.company?.role || null;

  return {
    setUserLanguage,
    setUserCompany,
    userLogout,
    companyId,
    currency,
    setUser,
    role,
    user,
  };
};
